import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem } from "@mui/material";
import React, { ReactNode } from "react";

import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { isEmpty, isNotEmpty } from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { MuiIconButton } from "../button/MuiButton";
import { MuiOptionMenuWrapper } from "./MuiOptionMenu.style";

export interface OptionMenuItemProps {
  onClick: () => void;
  dataTestId: string;
  label: string | ReactNode;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
}
export interface OptionMenuProps {
  menuItems: OptionMenuItemProps[];
  className?: string;
  shouldRenderEmpty?: boolean;
}

export const MuiOptionMenu: React.FC<OptionMenuProps> = ({
  menuItems,
  className,
  shouldRenderEmpty = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { commonARIALabel } = useNavigationSiteCore();
  const isMobile = useMediaQueries();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSelectAction = (action: OptionMenuItemProps) => {
    handleClose();
    action.onClick();
  };

  // If there are no menu items, don't render the option button/menu
  if (isEmpty(menuItems) && !shouldRenderEmpty) return null;

  return (
    <MuiOptionMenuWrapper className={className}>
      <MuiIconButton
        className={`option-menu-icon ${shouldRenderEmpty && (isNotEmpty(menuItems) ? "visible" : "hidden")}`}
        data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU}-action`}
        aria-hidden={open ? "true" : undefined}
        aria-label={commonARIALabel.moreOptionsForThisTableRow}
        onClick={handleClick}
      >
        <MoreVertIcon />
      </MuiIconButton>
      <Menu
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        {...(isMobile && {
          slotProps: {
            paper: {
              sx: {
                borderRadius: "8px",
              },
            },
          },
        })}
      >
        {menuItems.map((menuItem) => (
          <MenuItem
            key={menuItem.dataTestId}
            onClick={() => onSelectAction(menuItem)}
            data-testid={menuItem.dataTestId}
            className={className}
            aria-label={menuItem.ariaLabel}
            disabled={menuItem.disabled}
          >
            {menuItem.label}
          </MenuItem>
        ))}
      </Menu>
    </MuiOptionMenuWrapper>
  );
};
